<template>
    <search-layout
        v-if="loaded"
        class="activities-page"
        :map-expanded="mapExpanded"
        :offers-count="filteredOffers.length"
        :search-active="searchActive"
    >
        <template v-slot:breadcrumbs>
            <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="search" />
            <activity-breadcrumbs :search-request="searchRequest" />
        </template>
        <template v-slot:left-sidebar>
            <activity-search-summary :search-request="searchRequest" show-dates />
            <sort-select v-model="sortKey" :items="sortItems" />
            <group-offers-select class="pt-3" :items="['none', 'serviceName', 'date']" :product-store="productStore" />
            <activity-filters
                :filter-values="filterValues"
                :filters="searchResponse.filters"
                :filtered-offers-count="filteredOffers.length"
                :currency="currency"
                @change="changeFilter"
                @reset="resetFilters"
            />
        </template>
        <template v-slot:main-content>
            <search-progress
                :filtered-offers-count="filteredOffers.length"
                :total-offers-count="searchResponse.product.length"
                :search-active="searchActive"
                @stop="stopSearch"
            />
            <activity-offers :offers="offers" />
            <pagination v-model="page" :pages="pages" />
            <conditions-modal>
                <template v-slot:info>
                    <activity-conditions-modal-info />
                </template>
            </conditions-modal>
            <activity-info-modal />
        </template>
        <template v-slot:right-sidebar>
            <client-only>
                <activity-map v-model="mapExpanded" :offers="mapPoints" :city="city" />
            </client-only>
        </template>
        <overlay-progress :active="filterActive && !searchActive" />
    </search-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import MobileSearchPageTabs from '@/components/snippets/MobileSearchPageTabs.vue'
    import Pagination from '~/components/search/Pagination.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import ActivityBreadcrumbs from '@/components/activities/ActivityBreadcrumbs.vue'
    import ActivityOffers from '@/components/activities/search/offers/ActivityOffers.vue'
    import ActivityFilters from '@/components/activities/search/ActivityFilters.vue'
    import ActivitySearchSummary from '@/components/activities/search/ActivitySearchSummary.vue'
    import ActivityMap from '@/components/activities/search/ActivityMap.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'
    import {SearchPageBase} from '@/mixins/SearchPageBase.vue'
    import {ActivityStoreMixin} from '@/mixins/ActivityStoreMixin.vue'
    import {ActivityPageMixin} from '@/components/activities/mixins/ActivityPageMixin.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import SearchProgress from '~src/components/search/searchProgress.src'
    import SortSelect from '@/components/search/SortSelect.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import SearchLayout from '~src/components/parts/searchLayout.src'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import _Tmp_SearchPageBase, {Tmp_SearchPageBase} from '@/mixins/Tmp_SearchPageBase.vue'
    import OverlayProgress from '~/components/OverlayProgress.vue'
    import {useActivityRuntimeData} from '~/composables/activity'
    import {callComponentComputed} from '~/utils/vue-class-helpers'

    @Component({
        components: {
            OverlayProgress,
            ActivityConditionsModalInfo,
            SortSelect,
            SearchProgress,
            ExpiredOffersSnackbar,
            ActivityOffers,
            Pagination,
            MobileSearchPageTabs,
            ActivityBreadcrumbs,
            ActivityFilters,
            ActivitySearchSummary,
            ActivityMap,
            ActivityInfoModal,
            ConditionsModal,
            SearchLayout,
            GroupOffersSelect,
        },
        async setup() {
            const route = useRoute()
            await useAsyncData(`activity-${route.query.countryId}-${route.query.cityId}`, async () => {
                await useActivityRuntimeData(route)
                return true
            })
        },
    })
    export class ActivitiesPage extends mixins(
        ActivityPageMixin,
        ActivityStoreMixin,
        SearchPageBase,
        Tmp_SearchPageBase,
        SearchPageMixin
    ) {
        get mapPoints() {
            return this.filteredOffers.reduce((departures, product) => {
                product.offers.forEach(offer => {
                    if (offer.pickUpPoints.length) {
                        offer.pickUpPoints.forEach(point => {
                            if (
                                point.latitude &&
                                point.longitude &&
                                !departures.some(
                                    item => item.latitude === point.latitude && item.longitude === point.longitude
                                )
                            ) {
                                const data = {
                                    pointName: point.name,
                                    startTimes: offer.startTimes,
                                    latitude: point.latitude,
                                    longitude: point.longitude,
                                    name: product.info.name,
                                }
                                departures.push(data)
                            }
                        })
                    }
                })

                return departures
            }, [])
        }

        get city() {
            return this.productRuntimeStore.city
        }

        get sortItems() {
            return [
                {
                    text: this.$t('sort.recommended'),
                    value: 'recommended',
                },
                {
                    text: this.$t('sort.favorites'),
                    value: 'favorites',
                },
                ...callComponentComputed(this, _Tmp_SearchPageBase, 'sortItems'),
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
            ]
        }
    }

    export default ActivitiesPage
</script>
